/**
 * Widżet zgłoszeń błędów i feedbacku Ybug — z identyfikacją użytkownika.
 *
 * Włączany wyłącznie wtedy, gdy backend poda identyfikator projektu
 * (`YBUG_PROJECT_ID`); bez niego nic się nie ładuje. Do Ybug trafia tylko to,
 * co potrzebne do odtworzenia zgłoszenia:
 *  - kandydat: wewnętrzne ID sprawy i język — bez imienia, telefonu i e-maila
 *    (formularz zgłoszenia też bez wstępnie wpisanych danych osobowych),
 *  - konsultant: id, imię i nazwisko, e-mail, rola; formularz ma wpisane
 *    imię i e-mail, żeby nie przepisywać ich przy każdym zgłoszeniu,
 *  - oboje: środowisko (PROD/DEV), wersja builda, log konsoli i błędy
 *    obsłużone w kodzie (`reportError`).
 * Widżet mówi w języku interfejsu i przechodzi na nowy po jego zmianie.
 * U kandydata marki (City Drive, Udriver) przycisk ma jej kolory zamiast
 * czerwieni for apps ustawionej w panelu Ybug.
 */

export interface YbugConfig {
  id: string | null;
  environment: string;
  build: string;
}

export type YbugLanguage = "pl" | "en" | "ru" | "uk";

type YbugUser = Record<string, unknown>;

export interface YbugIdentity {
  user: YbugUser;
  /** Wstępnie wypełniony formularz — tylko w panelu (dane konsultanta). */
  feedback?: { name?: string | null; email?: string | null };
  language: YbugLanguage;
}

interface YbugApi {
  setUser?: (user: YbugUser) => void;
  log?: (type: string, value: unknown) => void;
  destroy?: () => void;
  boot?: () => void;
}

declare global {
  interface Window {
    ybug_settings?: Record<string, unknown>;
    Ybug?: YbugApi;
  }
}

/** Kolory przycisku zgłoszeń: tło i napis, osobno dla motywu jasnego i ciemnego. */
export interface YbugColors {
  light: { button: string; text: string };
  dark: { button: string; text: string };
}

let loaded = false;
let config: YbugConfig | null = null;
let language: YbugLanguage | null = null;
let colors: YbugColors | null = null;

const KOLOR = /^#[0-9a-f]{6}$/i;
const KLUCZE_KOLOROW = ["primary_color", "secondary_color", "dark_primary_color", "dark_secondary_color"];

/** Ustawienia Ybug dla kolorów marki; bez marki — nic, zostają kolory z panelu Ybug. */
function colorSettings(c: YbugColors | null): Record<string, string> {
  if (!c) return {};
  const pary: [string, string][] = [
    ["primary_color", c.light.button],
    ["secondary_color", c.light.text],
    ["dark_primary_color", c.dark.button],
    ["dark_secondary_color", c.dark.text],
  ];
  return Object.fromEntries(pary.filter(([, wartosc]) => KOLOR.test(wartosc)));
}

/** Ybug czyta ustawienia tylko przy starcie — zmianę w trakcie sesji robimy restartem. */
function restart(): void {
  if (window.Ybug?.destroy && window.Ybug?.boot) {
    window.Ybug.destroy();
    window.Ybug.boot();
  }
}

/**
 * Kolory przycisku w barwach marki procesu; `null` = classic (kolory z panelu Ybug).
 *
 * Marka i widżet przychodzą dwoma niezależnymi żądaniami (`/meta` i `/health`).
 * Gdy marka jest pierwsza, kolory idą w ustawieniach startowych; gdy widżet
 * zdążył wstać — podmieniamy je i uruchamiamy go ponownie.
 */
export function brandYbug(next: YbugColors | null): void {
  const bezZmian = JSON.stringify(next) === JSON.stringify(colors);
  colors = next;
  if (!loaded || bezZmian) return;
  const ustawienia: Record<string, unknown> = { ...(window.ybug_settings ?? {}) };
  // Widżet wpisuje po starcie kolory z panelu do `ybug_settings` — usuwamy je,
  // żeby przy przejściu na classic wróciły wartości z panelu, a nie stara marka.
  for (const klucz of KLUCZE_KOLOROW) delete ustawienia[klucz];
  window.ybug_settings = { ...ustawienia, ...colorSettings(next) };
  restart();
}

function withContext(user: YbugUser): YbugUser {
  return { ...user, environment: config?.environment, build: config?.build };
}

function feedbackOf(identity: YbugIdentity): Record<string, string> | undefined {
  const entries = Object.entries(identity.feedback ?? {}).filter(([, v]) => Boolean(v));
  return entries.length ? (Object.fromEntries(entries) as Record<string, string>) : undefined;
}

/**
 * Ustawia (lub aktualizuje) użytkownika i język. Widżet ładuje się przy
 * pierwszym wywołaniu; kolejne aktualizują go w miejscu.
 */
export function identifyYbug(cfg: YbugConfig | null | undefined, identity: YbugIdentity): void {
  if (!cfg?.id) return;
  config = cfg;
  const user = withContext(identity.user);
  const feedback = feedbackOf(identity);

  if (loaded) {
    window.ybug_settings = {
      ...(window.ybug_settings ?? {}),
      user,
      ...(feedback ? { feedback } : {}),
      language_override: identity.language,
    };
    window.Ybug?.setUser?.(user);
    if (identity.language !== language) {
      // Ybug czyta język tylko przy starcie — zmianę w trakcie sesji robimy
      // ponownym uruchomieniem widżetu z nowymi ustawieniami.
      language = identity.language;
      restart();
    }
    return;
  }

  loaded = true;
  language = identity.language;
  window.ybug_settings = {
    id: cfg.id,
    user,
    ...(feedback ? { feedback } : {}),
    // Nagrywanie konsoli włączone jawnie, nie domyślną wartością z panelu Ybug.
    console_log: true,
    language_override: identity.language,
    ...colorSettings(colors),
  };
  const script = document.createElement("script");
  script.type = "text/javascript";
  script.async = true;
  script.src = `https://widget.ybug.io/button/${cfg.id}.js`;
  document.head.appendChild(script);
}

/**
 * Błąd obsłużony w kodzie — trafia do logu zgłoszenia Ybug.
 *
 * Tylko to, co pozwala odtworzyć problem bez danych osobowych: co się działo
 * (`where`), status, metoda, ścieżka BEZ parametrów zapytania (wyszukiwarka
 * panelu niesie w nich telefony i nazwiska) i kod błędu z serwera. Treści
 * żądań i odpowiedzi nie logujemy.
 */
export function reportError(
  where: string,
  detail: { status?: number; method?: string; path?: string; code?: string } = {},
): void {
  if (!loaded || !window.Ybug?.log) return;
  try {
    const sciezka = detail.path?.split("?")[0];
    window.Ybug.log("error", { where, ...detail, path: sciezka });
  } catch {
    /* logowanie nie może samo wywrócić aplikacji */
  }
}

/** „jan.kowalski@firma.pl" → „Jan Kowalski" — panel nie przechowuje imienia osobno. */
export function nameFromEmail(email: string): string {
  const lokalna = email.split("@")[0] ?? email;
  return lokalna
    .split(/[._-]+/)
    .filter(Boolean)
    .map((czesc) => czesc.charAt(0).toUpperCase() + czesc.slice(1))
    .join(" ");
}
